import { useCallback, useState, type RefObject } from "react";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from "@lexical/list";
import { $createQuoteNode, HeadingNode } from "@lexical/rich-text";
import {
  $createCodeNode,
} from "@lexical/code";
import {
  $createParagraphNode,
  FORMAT_TEXT_COMMAND,
} from "lexical";
import { OPEN_LINK_DIALOG_COMMAND, $toggleBlockForSelection } from "@tgpe/editor-core";
import {
  Bold,
  Code,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  List,
  ListOrdered,
  MoreHorizontal,
  Music,
  Quote,
  Strikethrough,
  Underline,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useMediaPicker } from "../media/useMediaPicker.js";
import { useMediaStore } from "../../stores/media-store.js";

import { FormatDrawer } from "./FormatDrawer.js";

interface ToolbarProps {
  /** Anchor for the link popover (mounted by LinkInsertPlugin). */
  linkButtonRef: RefObject<HTMLButtonElement | null>;
}

const PRIMARY_BTN =
  "inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--tg-text)] transition-colors hover:bg-[var(--tg-secondary-bg)] active:bg-[var(--tg-secondary-bg)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--tg-link)]";

const DIVIDER = "mx-0.5 h-5 w-px bg-[var(--tg-secondary-bg)] opacity-60";

export function Toolbar({ linkButtonRef }: ToolbarProps) {
  const [editor] = useLexicalComposerContext();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const pickMedia = useMediaPicker();
  const attachment = useMediaStore((s) => s.attachment);
  const mediaSlotTaken = attachment !== null;

  const toggle = useCallback(
    (format: "bold" | "italic" | "underline" | "strikethrough") => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [editor],
  );

  const insertList = useCallback(
    (kind: "ul" | "ol") => {
      editor.dispatchCommand(
        kind === "ul"
          ? INSERT_UNORDERED_LIST_COMMAND
          : INSERT_ORDERED_LIST_COMMAND,
        undefined,
      );
    },
    [editor],
  );

  // Wrap only the selected line(s) — splitting at line breaks so the format
  // does not spill onto the whole paragraph/post (e.g. pasted multi-line text).
  // `() => false` keeps the existing "always wrap, no toggle-off" semantics.
  const wrapInQuote = useCallback(() => {
    editor.update(() => {
      $toggleBlockForSelection(() => false, $createQuoteNode);
    });
  }, [editor]);

  const wrapInCodeBlock = useCallback(() => {
    editor.update(() => {
      $toggleBlockForSelection(() => false, $createCodeNode);
    });
  }, [editor]);

  const clearBlock = useCallback(() => {
    editor.update(() => {
      $toggleBlockForSelection(() => false, $createParagraphNode);
    });
  }, [editor]);

  const removeLink = useCallback(() => {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  }, [editor]);

  const openLinkDialog = useCallback(() => {
    editor.dispatchCommand(OPEN_LINK_DIALOG_COMMAND, undefined);
  }, [editor]);

  // Reference to keep tree-shaking from dropping HeadingNode-related code.
  // (HeadingNode is registered via editorConfig; agents have been known to
  // strip it accidentally.)
  void HeadingNode;

  return (
    <>
      <div
        className="flex items-center gap-0.5 border-b border-[var(--tg-secondary-bg)] bg-[var(--tg-bg)] px-1.5 py-1.5"
        role="toolbar"
        aria-label="Text formatting"
      >
        <button
          type="button"
          aria-label="Bold"
          className={PRIMARY_BTN}
          onClick={() => {
            toggle("bold");
          }}
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          aria-label="Italic"
          className={PRIMARY_BTN}
          onClick={() => {
            toggle("italic");
          }}
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          aria-label="Underline"
          className={PRIMARY_BTN}
          onClick={() => {
            toggle("underline");
          }}
        >
          <Underline size={18} />
        </button>
        <button
          type="button"
          aria-label="Strikethrough"
          className={PRIMARY_BTN}
          onClick={() => {
            toggle("strikethrough");
          }}
        >
          <Strikethrough size={18} />
        </button>
        <span className={DIVIDER} />
        <button
          ref={linkButtonRef}
          type="button"
          aria-label="Insert link"
          className={PRIMARY_BTN}
          onClick={openLinkDialog}
        >
          <LinkIcon size={18} />
        </button>
        <span className={DIVIDER} />
        <button
          type="button"
          aria-label={t("media.attach_photo")}
          className={`${PRIMARY_BTN} disabled:opacity-40`}
          disabled={mediaSlotTaken}
          onClick={() => {
            void pickMedia("photo");
          }}
        >
          <ImageIcon size={18} />
        </button>
        <button
          type="button"
          aria-label={t("media.attach_audio")}
          className={`${PRIMARY_BTN} disabled:opacity-40`}
          disabled={mediaSlotTaken}
          onClick={() => {
            void pickMedia("audio");
          }}
        >
          <Music size={18} />
        </button>
        <span className={DIVIDER} />
        <button
          type="button"
          aria-label="More formatting"
          title="More formatting"
          className={`${PRIMARY_BTN} ml-auto`}
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          <MoreHorizontal size={18} />
        </button>
      </div>

      <FormatDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        items={[
          {
            id: "ul",
            label: "Bullet list",
            icon: <List size={18} />,
            onSelect: () => {
              insertList("ul");
            },
          },
          {
            id: "ol",
            label: "Numbered list",
            icon: <ListOrdered size={18} />,
            onSelect: () => {
              insertList("ol");
            },
          },
          {
            id: "quote",
            label: "Blockquote",
            icon: <Quote size={18} />,
            onSelect: wrapInQuote,
          },
          {
            id: "code-block",
            label: "Code block",
            icon: <Code size={18} />,
            onSelect: wrapInCodeBlock,
          },
          {
            id: "remove-link",
            label: "Remove link",
            icon: <LinkIcon size={18} />,
            onSelect: removeLink,
          },
          {
            id: "clear",
            label: "Clear formatting",
            icon: null,
            onSelect: clearBlock,
          },
        ]}
      />
    </>
  );
}
